import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Button } from '@mui/material'
import { useNotification } from '../../hooks/useNotification'
import services from '../../services'
import RelatorioHtmlFrame, { ContextoRelatorio, ResultadoConsulta, UsoNoRelatorio } from '../../components/RelatorioHtmlFrame'
import {
  BlocoRelatorio, ConfigRelatorio, FiltroFixo, FiltroTela, Leitura, clonar, definir, descreverFiltro, escreverConfig,
  ler, lerConfig, mostrarValor, mudancas, parecePercentual, usosDosAusentes
} from './configRelatorio'
import AvisoAusentes from './AvisoAusentes'
import ErrosDaPrevia, { useErrosDaPrevia } from './ErrosDaPrevia'
import { ItensAusentes, SEM_AUSENTES } from './ausentes'
import './abaDados.scss'

interface CampoDic { nome: string; tipo?: string | null; descricao?: string | null; formato?: string | null }
interface VersaoBase { id: number; numero: number; status: string; html?: string | null }

interface Props {
  dashboardId: number
  ambienteId: number | ''
  visivel: boolean
  dicionario: { colunas: CampoDic[]; medidas: CampoDic[] }
  emAndamento: VersaoBase | null
  versaoPublicadaId?: number | null
  proximoNumero: number
  /** A aba Relatório tem código ainda não salvo: as duas mexeriam no mesmo HTML. */
  codigoNaoSalvo: boolean
  consultar: (consulta: unknown) => Promise<ResultadoConsulta>
  /** Recarrega a página e abre a prévia da versão salva na aba Relatório. */
  aoSalvar: (versaoId: number) => Promise<void>
  /** Quantas mudanças ainda não salvas a aba tem: a aba Relatório avisa antes de enviar sem elas. */
  aoPendencias?: (quantas: number) => void
  /** O que o dicionário libera e o modelo não tem mais: aparece no topo, com o bloco de cada item, e nos campos que o usam. */
  ausentes?: ItensAusentes
}

const TEXTOS = '__textos'
const FILTROS = '__filtros'
const FORMATOS = [
  ['', 'Automático'], ['numero', 'Número'], ['percentual', 'Percentual'], ['moeda', 'Moeda (R$)'],
  ['compacto', 'Compacto (1,2 mil)'], ['data', 'Data']
]
const OPERADORES = ['=', '!=', '>', '>=', '<', '<=', 'in']
const TIPO_ARRASTE = 'application/x-portal-campo'

/**
 * Aba Dados do relatório HTML: clicar num bloco da prévia abre a configuração dele, e dá
 * para trocar medida, coluna, título, rótulos, formato, ordem, quantidade, filtros fixos e
 * se o bloco aparece. Tudo isso mora no bloco portal-relatorio do HTML; a troca reescreve
 * só ele e vira uma versão nova, que passa pela revisão como qualquer outra.
 */
export default function AbaDados(p: Props) {
  const { notify } = useNotification()
  const [base, setBase] = useState<{ html: string; versao: VersaoBase; editavel: boolean } | null>(null)
  const [carregandoBase, setCarregandoBase] = useState(true)
  const [leitura, setLeitura] = useState<Leitura | null>(null)
  const [config, setConfig] = useState<ConfigRelatorio>({})
  const [escolha, setEscolha] = useState<string | null>(null)
  const [foco, setFoco] = useState<string | null>(null)
  // geracao: sobe a cada prévia que chega. Vira a key do iframe, para ele recarregar mesmo quando
  // o HTML vem igual (troca de ambiente muda só o contexto).
  const [previa, setPrevia] = useState<{ html: string; contexto: ContextoRelatorio; geracao: number } | null>(null)
  const pedidoPrevia = useRef(0)
  const [erroPrevia, setErroPrevia] = useState('')
  const [salvando, setSalvando] = useState(false)
  // O que o código consultou na última carga da prévia. Só vale depois de dar tempo das consultas saírem.
  const [uso, setUso] = useState<{ blocos: string[]; soltas: { colunas: string[]; medidas: string[] }[]; medido: boolean }>({ blocos: [], soltas: [], medido: false })

  useEffect(() => {
    setUso({ blocos: [], soltas: [], medido: false })
    if (!previa) return
    const relogio = setTimeout(() => setUso(u => ({ ...u, medido: true })), 6000)
    return () => clearTimeout(relogio)
  }, [previa])

  // As consultas da prévia que falham ficam juntas no topo dela. A lista recomeça a cada prévia nova.
  const { consultar: consultarComErros, erros: errosDaPrevia } = useErrosDaPrevia(p.consultar, previa?.geracao)
  const itensAusentes = p.ausentes ?? SEM_AUSENTES
  const nomesAusentes = useMemo(() => [...itensAusentes.colunas, ...itensAusentes.medidas], [itensAusentes])
  const usos = useMemo(() => usosDosAusentes(config, nomesAusentes), [config, nomesAusentes])
  const blocosComItemAusente = useMemo(
    () => new Set(Object.values(usos).flat().filter(onde => onde.startsWith('bloco ')).map(onde => onde.slice('bloco '.length))),
    [usos]
  )

  const aoUso = useCallback((m: UsoNoRelatorio) => setUso(u => (m.tipo === 'bloco-usado'
    ? (u.blocos.includes(m.bloco) ? u : { ...u, blocos: [...u.blocos, m.bloco] })
    : u.soltas.some(s => JSON.stringify(s) === JSON.stringify({ colunas: m.colunas, medidas: m.medidas })) ? u
      : { ...u, soltas: [...u.soltas, { colunas: m.colunas, medidas: m.medidas }] })), [])

  // A base: o rascunho em andamento, se houver; senão, a versão publicada. Depende do que muda
  // de verdade (id, situação, HTML), não do objeto: salvar o dicionário recarrega a página e
  // traz um objeto novo, e isso não pode jogar fora as mudanças pendentes daqui.
  const em = p.emAndamento
  useEffect(() => {
    let vivo = true
    ;(async () => {
      if (em && em.html != null) {
        setBase({ html: em.html, versao: em, editavel: em.status === 'draft' || em.status === 'rejected' })
      } else if (p.versaoPublicadaId) {
        setCarregandoBase(true)
        const r: any = await services.api.relatorioHtmlVersao(p.versaoPublicadaId)
        if (!vivo) return
        if (r.fail) notify(r.error, { variant: 'error' })
        else setBase({ html: r.data.html ?? '', versao: r.data, editavel: true })
      } else {
        setBase(null)
      }
      if (vivo) setCarregandoBase(false)
    })()
    return () => { vivo = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [em?.id, em?.status, em?.html, p.versaoPublicadaId, notify])

  useEffect(() => {
    const l = base ? lerConfig(base.html) : null
    setLeitura(l)
    setConfig(l?.tipo === 'ok' ? clonar(l.config) : {})
    setEscolha(e => (l?.tipo === 'ok' && e && (e === TEXTOS || e === FILTROS || l.config.blocos?.[e]) ? e : null))
  }, [base])

  const original = leitura?.tipo === 'ok' ? leitura.config : null
  const pendentes = useMemo(() => (original ? mudancas(original, config) : []), [original, config])
  const htmlAtual = useMemo(() => (base && original ? escreverConfig(base.html, config) : base?.html ?? ''), [base, original, config])

  const { aoPendencias } = p
  useEffect(() => { aoPendencias?.(pendentes.length) }, [pendentes.length, aoPendencias])

  // Prévia ao vivo, com folga entre as mudanças (cada prévia roda todas as consultas). Só roda
  // com a aba aberta, e uma resposta atrasada não passa por cima de uma mais nova.
  useEffect(() => {
    if (!p.visivel || !base || p.ambienteId === '') return
    const relogio = setTimeout(async () => {
      const pedido = ++pedidoPrevia.current
      const r: any = await services.api.relatorioHtmlPreviaRascunho(p.dashboardId, p.ambienteId, htmlAtual)
      if (pedido !== pedidoPrevia.current) return
      if (r.fail) setErroPrevia(r.error)
      else {
        setErroPrevia('')
        setPrevia({ ...r.data, geracao: pedido })
      }
    }, 800)
    return () => clearTimeout(relogio)
  }, [p.visivel, p.dashboardId, p.ambienteId, base, htmlAtual])

  useEffect(() => {
    if (!foco) return
    const el = document.querySelector<HTMLElement>(`.aba-dados [data-campo="${CSS.escape(foco)}"]`)
    el?.focus()
    el?.scrollIntoView({ block: 'nearest' })
  }, [foco, escolha])

  const aoSelecionar = useCallback(({ bloco, texto, filtros }: { bloco: string | null; texto: string | null; filtros?: boolean }) => {
    if (filtros) {
      setEscolha(FILTROS)
      setFoco(null)
    } else if (texto && config.textos && Object.prototype.hasOwnProperty.call(config.textos, texto)) {
      setEscolha(TEXTOS)
      setFoco(`textos.${texto}`)
    } else if (bloco && config.blocos?.[bloco]) {
      setEscolha(bloco)
      setFoco(texto ? `blocos.${texto}` : null)
    }
  }, [config])

  const mudar = (caminho: string[], valor: unknown) => setConfig(c => definir(c, caminho, valor))
  const desfazer = (caminho: string[]) => {
    const v = ler(original, caminho)
    mudar(caminho, v === undefined ? undefined : clonar(v))
  }

  const salvar = async (enviar: boolean) => {
    if (!base) return
    setSalvando(true)
    try {
      const r: any = base.editavel && p.emAndamento?.id === base.versao.id
        ? await services.api.relatorioHtmlEditarVersao(base.versao.id, htmlAtual)
        : await services.api.relatorioHtmlCriarVersao(p.dashboardId, htmlAtual)
      if (r.fail) {
        notify(r.error, { variant: 'error' })
        return
      }
      const versao = r.data
      if (enviar) {
        const e: any = await services.api.relatorioHtmlAcao(versao.id, 'enviar')
        if (e.fail) notify(`A versão ${versao.numero} ficou salva como rascunho, mas não foi para revisão: ${e.error}`, { variant: 'warning' })
        else notify(`Versão ${versao.numero} enviada para revisão. A publicada continua no ar até a aprovação.`, { variant: 'success' })
      } else {
        notify(`Versão ${versao.numero} salva como rascunho.`, { variant: 'success' })
      }
      await p.aoSalvar(versao.id)
    } finally {
      setSalvando(false)
    }
  }

  if (carregandoBase) return <div className="aba-dados"><p className="ad-nota">Carregando…</p></div>
  if (!base) {
    return (
      <div className="aba-dados">
        <Alert severity="info">Ainda não há versão deste relatório. Crie a primeira na aba Relatório.</Alert>
      </div>
    )
  }
  if (leitura?.tipo === 'sem-bloco') {
    return (
      <div className="aba-dados">
        <Alert severity="info">
          Este HTML não tem o bloco <code>portal-relatorio</code>, então medidas, títulos e formatos estão escritos no código e
          não dá para trocar aqui. Peça ao Claude para refazer o relatório com o guia padrão novo (baixe na aba Dicionário) ou
          comece do modelo na aba Relatório.
        </Alert>
      </div>
    )
  }
  if (leitura?.tipo === 'invalido') {
    return (
      <div className="aba-dados">
        <Alert severity="error">O bloco <code>portal-relatorio</code> não é um JSON válido: {leitura.erro}. Corrija na aba Relatório.</Alert>
      </div>
    )
  }

  const somenteLeitura = !base.editavel || p.codigoNaoSalvo
  const blocos = config.blocos ?? {}
  const idsBlocos = Object.keys(blocos)
  const temTextos = Object.keys(config.textos ?? {}).length > 0
  const editandoRascunho = base.editavel && p.emAndamento?.id === base.versao.id

  return (
    <div className="aba-dados">
      <p className="ad-explica">
        Clique num bloco da prévia para abrir a configuração dele. Troque a medida escolhendo na lista ou arrastando do
        dicionário. As mudanças viram uma versão nova, que passa por revisão.
        <span className="ad-nota"> Base: {editandoRascunho ? `rascunho da versão ${base.versao.numero}` : `versão ${base.versao.numero}${p.versaoPublicadaId === base.versao.id ? ' (publicada)' : ''}`}.</span>
      </p>

      {!base.editavel && (
        <Alert severity="warning">A versão {base.versao.numero} está em revisão. Dá para ver a configuração, mas trocas só depois de aprovar ou recusar.</Alert>
      )}
      {base.editavel && p.codigoNaoSalvo && (
        <Alert severity="warning">Há mudanças não salvas no código, na aba Relatório. Salve lá antes de trocar dados aqui.</Alert>
      )}

      <AvisoAusentes ausentes={itensAusentes} usos={usos} />

      {uso.soltas.length > 0 && (
        <Alert severity="warning">
          O código faz {uso.soltas.length === 1 ? '1 consulta' : `${uso.soltas.length} consultas`} fora dos blocos, que a aba Dados não
          alcança: {uso.soltas.slice(0, 3).map(s => [...s.colunas, ...s.medidas].join(', ') || 'totais').join(' · ')}
          {uso.soltas.length > 3 ? ' …' : ''}. Peça ao Claude para cada desenho usar Portal.bloco(id).desenhar(...).
        </Alert>
      )}

      {pendentes.length > 0 && (
        <div className="ad-pendente" role="status">
          <b>{pendentes.length === 1 ? '1 mudança ainda não salva' : `${pendentes.length} mudanças ainda não salvas`}</b>
          <ul>
            {pendentes.map(m => (
              <li key={m.caminho.join('.')}>
                <code>{m.rotulo}</code>: {mostrarValor(m.de)} → <b>{mostrarValor(m.para)}</b>
                <button type="button" className="ad-link" onClick={() => desfazer(m.caminho)}>desfazer</button>
              </li>
            ))}
          </ul>
          <div className="ad-acoes">
            <Button size="small" onClick={() => original && setConfig(clonar(original))} disabled={salvando}>Desfazer tudo</Button>
            <Button size="small" variant="outlined" onClick={() => salvar(false)} disabled={salvando || somenteLeitura}>Só salvar rascunho</Button>
            <Button size="small" variant="contained" className="btn-purple" onClick={() => salvar(true)} disabled={salvando || somenteLeitura}>
              {editandoRascunho ? `Salvar na versão ${base.versao.numero} e enviar para revisão` : `Criar versão ${p.proximoNumero} para revisão`}
            </Button>
          </div>
        </div>
      )}

      <div className="ad-grade">
        <section className="ad-cartao" aria-label="Prévia do relatório">
          <div className="ad-cartao-cab">
            <h2>Prévia</h2>
            <span className="ad-dica">dados reais do ambiente escolhido, sem a regra por pessoa</span>
          </div>
          {p.ambienteId === '' && <Alert severity="warning" sx={{ m: 2 }}>Este relatório não está em nenhum ambiente: vincule um grupo para ter prévia com dados.</Alert>}
          {erroPrevia && <Alert severity="error" sx={{ m: 2 }}>{erroPrevia}</Alert>}
          <ErrosDaPrevia erros={errosDaPrevia} />
          <div className="ad-previa">
            {previa
              ? <RelatorioHtmlFrame key={previa.geracao} html={previa.html} contexto={previa.contexto} consultar={consultarComErros}
                                    aoSelecionar={aoSelecionar} destacar={escolha && escolha !== TEXTOS ? escolha : null} aoUso={aoUso} />
              : <p className="ad-nota" style={{ padding: 16 }}>Montando a prévia…</p>}
          </div>
        </section>

        <section className="ad-cartao ad-painel" aria-label="Configuração do bloco">
          <div className="ad-escolhas" role="tablist" aria-label="Blocos do relatório">
            {idsBlocos.map(id => (
              <button key={id} type="button" role="tab" aria-selected={escolha === id} onClick={() => { setEscolha(id); setFoco(null) }}
                      title={blocosComItemAusente.has(id) ? 'Usa um item que o modelo não tem mais: este bloco dá erro' : undefined}
                      className={`${pendentes.some(m => m.caminho[0] === 'blocos' && m.caminho[1] === id) ? 'mudou' : ''}${blocosComItemAusente.has(id) ? ' erro' : ''}`}>
                {id}
              </button>
            ))}
            <button type="button" role="tab" aria-selected={escolha === FILTROS} onClick={() => { setEscolha(FILTROS); setFoco(null) }}
                    className={pendentes.some(m => m.caminho[0] === 'filtros_tela') ? 'mudou' : ''}>
              Filtros da tela{(config.filtros_tela?.length ?? 0) > 0 ? ` (${config.filtros_tela!.length})` : ''}
            </button>
            {temTextos && (
              <button type="button" role="tab" aria-selected={escolha === TEXTOS} onClick={() => { setEscolha(TEXTOS); setFoco(null) }}
                      className={pendentes.some(m => m.caminho[0] === 'textos') ? 'mudou' : ''}>
                Textos da página
              </button>
            )}
          </div>

          <fieldset disabled={somenteLeitura} className="ad-campos">
            {!escolha && <p className="ad-nota">Clique num gráfico, tabela ou cartão da prévia, ou escolha um bloco acima.</p>}
            {escolha === TEXTOS && <PainelTextos config={config} mudar={mudar} />}
            {escolha === FILTROS && (
              <PainelFiltrosTela filtros={config.filtros_tela ?? []} colunas={p.dicionario.colunas}
                                 aoMudar={f => mudar(['filtros_tela'], f.length ? f : original?.filtros_tela ? [] : undefined)} />
            )}
            {escolha && escolha !== TEXTOS && blocos[escolha] && (
              <PainelBloco id={escolha} bloco={blocos[escolha]} original={original?.blocos?.[escolha]} dicionario={p.dicionario}
                           semConsulta={uso.medido && !uso.blocos.includes(escolha)} filtrosTela={config.filtros_tela ?? []}
                           mudar={(caminho, valor) => mudar(['blocos', escolha, ...caminho], valor)} consultar={p.consultar} ausentes={nomesAusentes} />
            )}
          </fieldset>
        </section>
      </div>
    </div>
  )
}

function PainelTextos({ config, mudar }: { config: ConfigRelatorio; mudar: (c: string[], v: unknown) => void }) {
  const textos = config.textos ?? {}
  return (
    <>
      <h3 className="ad-titulo">Textos da página</h3>
      {Object.keys(textos).map(k => (
        <label key={k} className="ad-campo">
          <span className="ad-rot">{k}</span>
          {String(textos[k] ?? '').length > 60
            ? <textarea data-campo={`textos.${k}`} rows={3} value={textos[k] ?? ''} onChange={e => mudar(['textos', k], e.target.value)} />
            : <input data-campo={`textos.${k}`} value={textos[k] ?? ''} onChange={e => mudar(['textos', k], e.target.value)} />}
        </label>
      ))}
    </>
  )
}

interface PainelBlocoProps {
  id: string
  /** Na prévia, o código não chamou a consulta deste bloco: o desenho vem de outro lugar. */
  semConsulta: boolean
  filtrosTela: FiltroTela[]
  bloco: BlocoRelatorio
  original?: BlocoRelatorio
  dicionario: { colunas: CampoDic[]; medidas: CampoDic[] }
  mudar: (caminho: string[], valor: unknown) => void
  consultar: (consulta: unknown) => Promise<ResultadoConsulta>
  /** Itens do dicionário que o modelo não tem mais. */
  ausentes: string[]
}

function PainelBloco({ id, bloco, original, dicionario, mudar, consultar, semConsulta, filtrosTela, ausentes }: PainelBlocoProps) {
  const semNoModelo = (nome: string) => ausentes.some(a => a.trim().toLowerCase() === nome.trim().toLowerCase())
  const q = bloco.consulta ?? {}
  const colunas = Object.entries(q.colunas ?? {})
  const medidas = Object.entries(q.medidas ?? {})
  const apelidos = [...colunas.map(c => c[0]), ...medidas.map(m => m[0])]
  const [alvo, setAlvo] = useState<string | null>(null)
  const [busca, setBusca] = useState('')
  const [teste, setTeste] = useState<{ linhas: Record<string, unknown>[]; erro?: string } | null>(null)

  useEffect(() => setTeste(null), [id, bloco])

  const medidaDic = (nome: string) => dicionario.medidas.find(m => m.nome === nome)
  const colunaDic = (nome: string) => dicionario.colunas.find(c => c.nome === nome)

  const avisoMedida = (apelido: string, nome: string): string | null => {
    const dic = medidaDic(nome)
    if (!dic) return `“${nome}” não está no dicionário: a consulta vai falhar.`
    const outro = medidas.find(([a, m]) => a !== apelido && m === nome)
    if (outro) return `A mesma medida está em “${outro[0]}”: as duas séries vão mostrar o mesmo número.`
    const tipo = bloco.formatos?.[apelido]?.tipo
    const pct = parecePercentual(nome) || /%/.test(dic.formato ?? '')
    if (tipo === 'percentual' && !pct) return `O formato é percentual, e “${nome}” parece ser um número. Confira.`
    if (tipo && tipo !== 'percentual' && pct) return `“${nome}” parece ser percentual, e o formato está como ${tipo}. Confira.`
    return null
  }

  const testar = async () => {
    const nomes: string[] = []
    medidas.forEach(([, m]) => { if (!nomes.includes(m)) nomes.push(m) })
    const nomeDe = (a?: string) => (a ? (q.colunas?.[a] ?? q.medidas?.[a] ?? a) : undefined)
    try {
      const r = await consultar({ colunas: colunas.map(c => c[1]), medidas: nomes, filtros: q.filtros ?? [],
        ordenarPor: nomeDe(q.ordenarPor), direcao: q.direcao, limite: q.limite })
      setTeste({ linhas: r.linhas.slice(0, 5).map(l => Object.fromEntries([...colunas, ...medidas].map(([a, n]) => [a, l[n]]))) })
    } catch (e: any) {
      setTeste({ linhas: [], erro: e?.message ?? 'Falha ao consultar.' })
    }
  }

  const soltar = (e: React.DragEvent, tipo: 'coluna' | 'medida', apelido: string) => {
    e.preventDefault()
    setAlvo(null)
    try {
      const campo = JSON.parse(e.dataTransfer.getData(TIPO_ARRASTE))
      if (campo.tipo === tipo) mudar(['consulta', tipo === 'coluna' ? 'colunas' : 'medidas', apelido], campo.nome)
    } catch { /* arrastou outra coisa */ }
  }

  const mudou = (caminho: string[]) => JSON.stringify(ler(bloco, caminho)) !== JSON.stringify(ler(original, caminho))
  const dicFiltrado = [
    ...dicionario.medidas.map(m => ({ ...m, tipoCampo: 'medida' as const })),
    ...dicionario.colunas.map(c => ({ ...c, tipoCampo: 'coluna' as const }))
  ].filter(c => c.nome.toLowerCase().includes(busca.toLowerCase()))

  const slot = (tipo: 'coluna' | 'medida', apelido: string, nome: string) => {
    const lista = tipo === 'coluna' ? dicionario.colunas : dicionario.medidas
    const dic = tipo === 'coluna' ? colunaDic(nome) : medidaDic(nome)
    const aviso = semNoModelo(nome)
      ? `“${nome}” não existe mais no modelo: este bloco dá erro até você escolher outro item.`
      : tipo === 'medida' ? avisoMedida(apelido, nome) : dic ? null : `“${nome}” não está no dicionário: a consulta vai falhar.`
    const caminho = ['consulta', tipo === 'coluna' ? 'colunas' : 'medidas', apelido]
    const formato = bloco.formatos?.[apelido] ?? {}
    return (
      <div key={tipo + apelido}
           className={`ad-slot${alvo === apelido ? ' alvo' : ''}${mudou(caminho) || mudou(['rotulos', apelido]) || mudou(['formatos', apelido]) ? ' mudou' : ''}`}
           onDragOver={e => { e.preventDefault(); setAlvo(apelido) }} onDragLeave={() => setAlvo(null)} onDrop={e => soltar(e, tipo, apelido)}>
        <div className="ad-apelido">{apelido}<small>{tipo}</small></div>
        <div className="ad-slot-campos">
          <select aria-label={`${tipo === 'coluna' ? 'Coluna' : 'Medida'} do apelido ${apelido}`} value={nome}
                  onChange={e => mudar(caminho, e.target.value)}>
            {!lista.some(c => c.nome === nome) && <option value={nome}>{nome} (fora do dicionário)</option>}
            {lista.map(c => <option key={c.nome} value={c.nome}>{c.nome}{semNoModelo(c.nome) ? ' (não existe mais no modelo)' : ''}</option>)}
          </select>
          {dic?.descricao && <div className="ad-desc">{dic.descricao}</div>}
          {aviso && <div className="ad-aviso">{aviso}</div>}
          <div className="ad-linha">
            <label className="ad-mini">Rótulo
              <input data-campo={`blocos.${id}.rotulos.${apelido}`} value={bloco.rotulos?.[apelido] ?? ''} placeholder={apelido}
                     onChange={e => mudar(['rotulos', apelido], e.target.value === '' ? undefined : e.target.value)} />
            </label>
            {tipo === 'medida' && (
              <>
                <label className="ad-mini">Formato
                  <select value={formato.tipo ?? ''} onChange={e => mudar(['formatos', apelido, 'tipo'], e.target.value || undefined)}>
                    {FORMATOS.map(([v, r]) => <option key={v} value={v}>{r}</option>)}
                  </select>
                </label>
                <label className="ad-mini ad-curto">Casas
                  <input type="number" min={0} max={6} value={formato.casas ?? ''}
                         onChange={e => mudar(['formatos', apelido, 'casas'], e.target.value === '' ? undefined : Math.max(0, Math.min(6, Number(e.target.value))))} />
                </label>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div>
        <span className="ad-id">{id}</span>
        {bloco.forma && <span className="ad-pill">{bloco.forma}</span>}
      </div>
      {semConsulta && (
        <div className="ad-aviso">
          Na prévia, o código deste desenho não buscou os dados pelo bloco: ele usa as linhas de outra consulta. Trocar
          eixo, medida, ordem ou filtro aqui não muda o desenho. Peça ao Claude para ele desenhar com
          Portal.bloco("{id}").desenhar(...).
        </div>
      )}
      <label className="ad-campo">
        <span className="ad-rot">Título</span>
        <input data-campo={`blocos.${id}.titulo`} value={bloco.titulo ?? ''} onChange={e => mudar(['titulo'], e.target.value)} />
      </label>
      <label className="ad-check">
        <input type="checkbox" checked={bloco.visivel !== false} onChange={e => mudar(['visivel'], e.target.checked ? (original?.visivel === undefined ? undefined : true) : false)} />
        Aparece no relatório
      </label>

      {colunas.length > 0
        ? <div><div className="ad-secao">Eixo e colunas</div>{colunas.map(([a, n]) => slot('coluna', a, n))}</div>
        : <p className="ad-nota">Sem colunas: uma linha de totais.</p>}
      <div><div className="ad-secao">Medidas</div>{medidas.map(([a, n]) => slot('medida', a, n))}</div>

      {colunas.length > 0 && (
        <div>
          <div className="ad-secao">Ordem e quantidade</div>
          <div className="ad-linha">
            <label className="ad-mini">Ordenar por
              <select value={q.ordenarPor ?? ''} onChange={e => mudar(['consulta', 'ordenarPor'], e.target.value || undefined)}>
                <option value="">Sem ordem definida</option>
                {apelidos.map(a => <option key={a} value={a}>{bloco.rotulos?.[a] ? `${bloco.rotulos[a]} (${a})` : a}</option>)}
              </select>
            </label>
            <label className="ad-mini">Direção
              <select value={q.direcao ?? 'DESC'} onChange={e => mudar(['consulta', 'direcao'], e.target.value)}>
                <option value="DESC">Maior primeiro</option>
                <option value="ASC">Menor primeiro</option>
              </select>
            </label>
            <label className="ad-mini ad-curto">Linhas
              <input type="number" min={1} placeholder="todas" value={q.limite ?? ''}
                     onChange={e => mudar(['consulta', 'limite'], e.target.value === '' ? undefined : Math.max(1, Math.floor(Number(e.target.value))))} />
            </label>
          </div>
        </div>
      )}

      {filtrosTela.length > 0 && (
        <div>
          <div className="ad-secao">Filtros da tela que este bloco segue</div>
          {filtrosTela.map(f => {
            const ignora = bloco.ignorarFiltros === true || (Array.isArray(bloco.ignorarFiltros) && bloco.ignorarFiltros.includes(f.id))
            return (
              <label key={f.id} className="ad-check">
                <input type="checkbox" checked={!ignora} onChange={e => {
                  const atual = bloco.ignorarFiltros === true ? filtrosTela.map(x => x.id) : Array.isArray(bloco.ignorarFiltros) ? bloco.ignorarFiltros : []
                  const novo = e.target.checked ? atual.filter(x => x !== f.id) : Array.from(new Set([...atual, f.id]))
                  mudar(['ignorarFiltros'], novo.length ? novo : undefined)
                }} />
                {f.rotulo || f.id}
              </label>
            )
          })}
        </div>
      )}

      <Filtros filtros={q.filtros ?? []} colunas={dicionario.colunas} aoMudar={f => mudar(['consulta', 'filtros'], f.length ? f : original?.consulta?.filtros ? [] : undefined)} />

      <div>
        <Button size="small" variant="outlined" onClick={testar}>Testar a consulta</Button>
        {teste?.erro && <div className="ad-aviso">{teste.erro}</div>}
        {teste && !teste.erro && (
          <div className="ad-teste">
            {teste.linhas.length === 0 ? <p className="ad-nota">Nenhuma linha neste recorte.</p> : (
              <table>
                <thead><tr>{apelidos.map(a => <th key={a}>{a}</th>)}</tr></thead>
                <tbody>{teste.linhas.map((l, i) => <tr key={i}>{apelidos.map(a => <td key={a}>{l[a] == null ? '—' : String(l[a])}</td>)}</tr>)}</tbody>
              </table>
            )}
          </div>
        )}
      </div>

      <div>
        <div className="ad-secao">Dicionário · arraste para um apelido</div>
        <input className="ad-busca" placeholder="Buscar no dicionário" value={busca} onChange={e => setBusca(e.target.value)} />
        <div className="ad-dic">
          {dicFiltrado.map(c => (
            <div key={c.tipoCampo + c.nome} className="ad-dic-item" draggable title={c.descricao ?? ''}
                 onDragStart={e => e.dataTransfer.setData(TIPO_ARRASTE, JSON.stringify({ tipo: c.tipoCampo, nome: c.nome }))}>
              <span className="ad-pega" aria-hidden="true">⋮⋮</span><b>{c.nome}</b><small>{c.tipoCampo}</small>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

const nomeCurto = (nome: string) => (nome.includes('[') ? nome.slice(nome.indexOf('[') + 1, nome.lastIndexOf(']')) : nome)
const idDe = (texto: string) => texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'filtro'

/**
 * Os filtros da barra que o portal desenha no relatório. Todos são múltipla escolha com busca
 * e valem em todos os blocos, salvo os que o bloco desmarca.
 */
function PainelFiltrosTela({ filtros, colunas, aoMudar }: { filtros: FiltroTela[]; colunas: CampoDic[]; aoMudar: (f: FiltroTela[]) => void }) {
  const [nova, setNova] = useState('')
  const livres = colunas.filter(c => !filtros.some(f => f.coluna === c.nome))
  const trocar = (i: number, f: Partial<FiltroTela>) => aoMudar(filtros.map((x, j) => (j === i ? { ...x, ...f } : x)))
  const mover = (i: number, d: number) => {
    const n = [...filtros]
    const [x] = n.splice(i, 1)
    n.splice(i + d, 0, x)
    aoMudar(n)
  }
  const adicionar = () => {
    const coluna = nova || livres[0]?.nome
    if (!coluna) return
    const base = idDe(nomeCurto(coluna))
    const usados = new Set(filtros.map(f => f.id))
    let id = base, k = 2
    while (usados.has(id)) id = `${base}_${k++}`
    aoMudar([...filtros, { id, coluna, rotulo: nomeCurto(coluna) }])
    setNova('')
  }
  return (
    <>
      <h3 className="ad-titulo">Filtros da tela</h3>
      <p className="ad-nota">
        O portal desenha a barra no relatório: múltipla escolha, com busca, "Selecionar todos" e "Limpar". Cada filtro vale em
        todos os blocos; para um bloco não seguir um filtro, desmarque no próprio bloco.
      </p>
      {filtros.length === 0 && <p className="ad-nota">Nenhum filtro na tela.</p>}
      {filtros.map((f, i) => (
        <div key={f.id} className="ad-slot">
          <div className="ad-apelido">{f.id}<small>filtro</small></div>
          <div className="ad-slot-campos">
            <select aria-label={`Coluna do filtro ${f.id}`} value={f.coluna} onChange={e => trocar(i, { coluna: e.target.value })}>
              {!colunas.some(c => c.nome === f.coluna) && <option value={f.coluna}>{f.coluna} (fora do dicionário)</option>}
              {colunas.filter(c => c.nome === f.coluna || !filtros.some(x => x.coluna === c.nome)).map(c => <option key={c.nome} value={c.nome}>{c.nome}</option>)}
            </select>
            <div className="ad-linha">
              <label className="ad-mini">Rótulo
                <input value={f.rotulo ?? ''} placeholder={nomeCurto(f.coluna)} onChange={e => trocar(i, { rotulo: e.target.value || undefined })} />
              </label>
            </div>
            <div>
              <button type="button" className="ad-link" disabled={i === 0} onClick={() => mover(i, -1)}>subir</button>
              <button type="button" className="ad-link" disabled={i === filtros.length - 1} onClick={() => mover(i, 1)}>descer</button>
              <button type="button" className="ad-link" onClick={() => aoMudar(filtros.filter((_, j) => j !== i))}>remover</button>
            </div>
          </div>
        </div>
      ))}
      {livres.length > 0 ? (
        <div className="ad-linha">
          <select aria-label="Coluna do novo filtro" value={nova || livres[0].nome} onChange={e => setNova(e.target.value)} style={{ flex: '1 1 200px' }}>
            {livres.map(c => <option key={c.nome} value={c.nome}>{c.nome}</option>)}
          </select>
          <Button size="small" variant="outlined" onClick={adicionar}>Adicionar filtro</Button>
        </div>
      ) : <p className="ad-nota">Todas as colunas do dicionário já são filtro. Libere outras na aba Dicionário.</p>}
    </>
  )
}

/** Filtros fixos do bloco. O valor é texto na tela e vira número, sim/não ou lista ao sair do campo. */
function Filtros({ filtros, colunas, aoMudar }: { filtros: FiltroFixo[]; colunas: CampoDic[]; aoMudar: (f: FiltroFixo[]) => void }) {
  const trocar = (i: number, f: Partial<FiltroFixo>) => aoMudar(filtros.map((x, j) => (j === i ? { ...x, ...f } : x)))
  return (
    <div>
      <div className="ad-secao">Filtros fixos</div>
      {filtros.length === 0 && <p className="ad-nota">Nenhum. O bloco usa só os filtros do portal e do ambiente.</p>}
      {filtros.map((f, i) => (
        <div key={i} className="ad-filtro" title={descreverFiltro(f)}>
          <select aria-label="Coluna do filtro" value={f.coluna} onChange={e => trocar(i, { coluna: e.target.value })}>
            {!colunas.some(c => c.nome === f.coluna) && <option value={f.coluna}>{f.coluna || 'Escolha a coluna'}</option>}
            {colunas.map(c => <option key={c.nome} value={c.nome}>{c.nome}</option>)}
          </select>
          <select aria-label="Operador" value={f.operador || '='} onChange={e => trocar(i, { operador: e.target.value, valor: converter(textoDoValor(f.valor), e.target.value) })}>
            {OPERADORES.map(o => <option key={o} value={o}>{o === 'in' ? 'em (lista)' : o}</option>)}
          </select>
          <ValorDoFiltro valor={f.valor} operador={f.operador || '='} aoMudar={v => trocar(i, { valor: v })} />
          <button type="button" className="ad-link" onClick={() => aoMudar(filtros.filter((_, j) => j !== i))}>remover</button>
        </div>
      ))}
      <button type="button" className="ad-link" disabled={colunas.length === 0}
              onClick={() => aoMudar([...filtros, { coluna: colunas[0]?.nome ?? '', operador: '=', valor: '' }])}>
        + adicionar filtro
      </button>
    </div>
  )
}

const textoDoValor = (v: unknown) => (Array.isArray(v) ? v.join(', ') : v == null ? '' : String(v))

function converter(texto: string, operador: string): unknown {
  const um = (t: string): unknown => {
    const s = t.trim()
    if (/^-?\d+(,\d+|\.\d+)?$/.test(s)) return Number(s.replace(',', '.'))
    if (s === 'true' || s === 'false') return s === 'true'
    return s
  }
  return operador === 'in' ? texto.split(';').flatMap(p => p.split(',')).map(um).filter(v => v !== '') : um(texto)
}

function ValorDoFiltro({ valor, operador, aoMudar }: { valor: unknown; operador: string; aoMudar: (v: unknown) => void }) {
  const [texto, setTexto] = useState(textoDoValor(valor))
  useEffect(() => setTexto(textoDoValor(valor)), [valor])
  return (
    <input aria-label="Valor do filtro" value={texto} placeholder={operador === 'in' ? 'SP, RJ, MG' : 'valor'}
           onChange={e => setTexto(e.target.value)} onBlur={() => aoMudar(converter(texto, operador))} />
  )
}
